/**
 * Data Protection (DPA) assessment catalog (Preparation).
 * Rule-based derivation only — no numeric score or risk band.
 * When the backend data-protection endpoint exists, prefer displaying
 * dpaRequired / piaRequired / tiaRequired from the API response.
 */

export type DataProtectionAnswers = Record<string, boolean | undefined>;

export type DataProtectionQuestion = {
  code: string;
  number: number;
  text: string;
};

export type DataProtectionFlags = {
  dpaRequired: boolean;
  piaRequired: boolean;
  tiaRequired: boolean;
};

export const DATA_PROTECTION_QUESTIONS: DataProtectionQuestion[] = [
  {
    code: 'DP_Q1',
    number: 1,
    text: 'In delivering PoC services to ISS, will your organization handle or manage any personal data, aside from the routine processing of business contact information?',
  },
  {
    code: 'DP_Q2',
    number: 2,
    text: 'Will your organization process special categories of Personal Data (e.g. health, racial or ethnic origin, political opinions, etc.) when providing services to our company?',
  },
  {
    code: 'DP_Q3',
    number: 3,
    text: 'To the best of your knowledge, will your organization process personal data of third parties affiliated with ISS (such as employees of ISS customers) during the provision of services?',
  },
  {
    code: 'DP_Q4',
    number: 4,
    text: 'Will you transfer personal data to entities (sub-contractors or your other affiliates) outside the EU/EEA or provide remote access to data from outside the EU/EEA when providing services to our company?',
  },
];

export function isDataProtectionComplete(
  answers: DataProtectionAnswers
): boolean {
  return DATA_PROTECTION_QUESTIONS.every((q) => answers[q.code] !== undefined);
}

/**
 * Derive required actions from Yes answers.
 * DPA = Q1 or Q2; PIA = Q2 or Q3; TIA = Q4.
 */
export function deriveDataProtection(
  answers: DataProtectionAnswers
): DataProtectionFlags {
  const q1 = answers.DP_Q1 === true;
  const q2 = answers.DP_Q2 === true;
  const q3 = answers.DP_Q3 === true;
  const q4 = answers.DP_Q4 === true;

  return {
    dpaRequired: q1 || q2,
    piaRequired: q2 || q3,
    tiaRequired: q4,
  };
}

export function getTriggeredActions(flags: DataProtectionFlags): string[] {
  const actions: string[] = [];
  if (flags.dpaRequired) {
    actions.push('DPA required (Data Processing Agreement)');
  }
  if (flags.piaRequired) {
    actions.push('PIA required (System Privacy Impact Assessment)');
  }
  if (flags.tiaRequired) {
    actions.push('TIA required (Transfer Impact Assessment)');
  }
  return actions;
}
